import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Flex, Text, Button, Image } from 'rebass';
import { Input } from '@rebass/forms';
import { useParams } from 'react-router-dom';
import { useWindowSize } from 'react-use';
import useSWR from 'swr';
import { getSWR, patch, get } from 'utils/api';
import store from 'store';
import Modal from 'app/modal/v3/index';
import { CgRuler } from 'react-icons/cg';
import { IoMdCheckmark, IoMdCheckmarkCircle } from 'react-icons/io';
import { IoSettingsOutline, IoChevronDownOutline, IoChevronUpOutline } from 'react-icons/io5';
import { nestImageLayers } from 'collections/book-creation/components/RenderFrontCover';
import ImageLayer from 'app/payment/ImageLayer';
import TextLayer from 'app/payment/TextLayer';
import useEntity from 'entities/hooks/use-entity';
import useHistory from 'app/hooks/history';
import { IoIosArrowBack } from 'react-icons/io';
import Loading from 'app/loading/index';
import PaymentBlocker from 'collections/milestones/PaymentBlocker';

const bookPreviewIcon = (fill = '#9B59B6') => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M18.125 3.12501C15.5921 3.1366 13.8267 3.45527 12.6392 4.02263C11.513 4.5607 10.8991 5.32445 10.625 6.33464V16.0311C12.0489 14.9777 14.232 14.375 18.125 14.375V3.12501ZM19.375 14.375C19.375 14.7065 19.2432 15.0244 19.0088 15.2588C18.7744 15.4932 18.4565 15.625 18.125 15.625C13.199 15.625 11.5058 16.6186 10.4883 17.8906C10.3697 18.0389 10.1899 18.125 10 18.125C9.81008 18.125 9.63029 18.0382 9.51172 17.8898C8.50105 16.6254 6.80029 15.625 1.875 15.625C1.49801 15.625 1.16777 15.4651 0.941571 15.1994C0.723464 14.943 0.625002 14.6174 0.625002 14.2993V3.12501C0.624694 2.96118 0.656986 2.7988 0.719403 2.64731C0.782118 2.49515 0.87403 2.35678 0.990399 2.24041L1.08155 2.1574C1.17663 2.07947 1.28323 2.01643 1.3973 1.96941C1.54952 1.90669 1.71281 1.87439 1.87744 1.87501L2.35921 1.88071C4.72867 1.92833 6.55579 2.25271 7.89958 2.8947C8.83239 3.34038 9.52557 3.93745 10 4.67774C10.4744 3.93745 11.1676 3.34038 12.1004 2.8947C13.5338 2.20989 15.5176 1.88669 18.1226 1.87501L18.2454 1.88071C18.3271 1.88847 18.4078 1.9043 18.4863 1.92791L18.6027 1.96941L18.7142 2.02149C18.7865 2.06004 18.8551 2.10544 18.9185 2.1574L19.0096 2.24041L19.0926 2.33155C19.1705 2.42663 19.2336 2.53323 19.2806 2.64731C19.343 2.7988 19.3753 2.96117 19.375 3.12501V14.375ZM1.875 14.2993C1.875 14.3368 1.88187 14.3612 1.88721 14.375C5.76997 14.3763 7.95382 14.9823 9.375 16.0319V6.33464C9.10089 5.32445 8.48704 4.5607 7.36084 4.02263C6.17333 3.45527 4.4079 3.1366 1.875 3.12501V14.2993Z" fill={fill} />
  </svg>
);

const FALLBACK_MONTHS = [
  'Newborn', '1 month', '2 months', '3 months', '4 months', '5 months',
  '6 months', '7 months', '8 months', '9 months', '10 months', '11 months', '12 months',
];

function UnitToggle({ value, onChange, options }) {
  return (
    <Flex sx={{ borderRadius: '20px', border: '1px solid #64748B', width: '100px' }}>
      {options.map((opt, index) => (
        <Box
          key={opt}
          as="button"
          onClick={() => onChange(opt)}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flex: 1,
            ml: value === opt && index === 0 && -1,
            mr: value === opt && index % 2 === 1 && -1,
            py: '6px',
            borderRadius: '23px',
            color: value === opt ? '#724086' : '#64748B',
            border: value === opt ? '2px solid #724086' : 'none',
            background: value === opt ? '#F5EEF8' : 'transparent',
            cursor: 'pointer',
            fontFamily: 'Poppins',
            boxShadow: value === opt ? '0 2px 2px 0 rgba(114, 64, 134, 0.25)' : 'unset',
            fontSize: '16px',
            minWidth: value === opt ? '55px' : '45px',
            fontWeight: 500,
            outline: 'none',
          }}
        >
          {opt}
        </Box>
      ))}
    </Flex>
  );
}

function GrowthChartFTUE({ onClose }) {
  const [heightUnit, setHeightUnit] = useState('in');
  const [weightUnit, setWeightUnit] = useState('lbs');

  return (
    <Modal maxWidth={420} shadeProps={{ pt: [0, 4] }} closeOnOutsideClick={false}>
      <Flex flexDirection="column" alignItems="center" p={[3, 4]} maxWidth={400} m="auto" mb={3}>
        <CgRuler size={40} color="#9B59B6" />
        <Text
          mt={3}
          fontFamily="Bitter"
          fontSize={['22px', '26px']}
          fontWeight={500}
          color="#0E0E0E"
          textAlign="center"
          lineHeight="130%"
        >
          Your Baby's Growth Chart
        </Text>
        <Text
          mt={2}
          fontFamily="Poppins"
          fontSize="16px"
          color="#64748B"
          textAlign="center"
          lineHeight="165%"
          px={2}
        >
          Record your baby's height and weight each month. Every measurement you add brings your keepsake chart page to life!
        </Text>
        <Text mt={3} mb={2} fontFamily="Poppins" fontSize="14px" fontWeight={600} color="#1E293B" textAlign="center">
          Select your unit preference:
        </Text>
        <Flex alignItems="center" mb={2}>
          <Text fontFamily="Poppins" fontSize="14px" color="#64748B" mr={3} sx={{ minWidth: '50px' }}>Height</Text>
          <UnitToggle value={heightUnit} onChange={setHeightUnit} options={['in', 'cm']} />
        </Flex>
        <Flex alignItems="center" mb={4}>
          <Text fontFamily="Poppins" fontSize="14px" color="#64748B" mr={3} sx={{ minWidth: '50px' }}>Weight</Text>
          <UnitToggle value={weightUnit} onChange={setWeightUnit} options={['lbs', 'kg']} />
        </Flex>
        <Button
          bg="#9B59B6"
          color="white"
          fontFamily="Poppins"
          fontSize="16px"
          sx={{ borderRadius: '23px' }}
          px={5}
          py={2}
          onClick={() => onClose({ heightUnit, weightUnit })}
        >
          Start Tracking
        </Button>
      </Flex>
    </Modal>
  );
}

function UnitsPanel({ heightUnit, weightUnit, onHeightUnit, onWeightUnit, onClose }) {
  const panelRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <Box
      ref={panelRef}
      sx={{
        position: 'absolute',
        right: 0,
        top: '32px',
        bg: 'white',
        border: '1px solid #E2E8F0',
        borderRadius: '12px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        p: 3,
        zIndex: 10,
        minWidth: '200px',
      }}
    >
      <Text fontFamily="Poppins" fontSize="16px" fontWeight={600} color="#1E293B" mb={2}>
        Units
      </Text>
      <Flex alignItems="center" mb={2}>
        <Text fontFamily="Poppins" fontSize="16px" color="#64748B" mr={2} sx={{ minWidth: '50px' }}>Height</Text>
        <UnitToggle value={heightUnit} onChange={onHeightUnit} options={['in', 'cm']} />
      </Flex>
      <Flex alignItems="center">
        <Text fontFamily="Poppins" fontSize="16px" color="#64748B" mr={2} sx={{ minWidth: '50px' }}>Weight</Text>
        <UnitToggle value={weightUnit} onChange={onWeightUnit} options={['lbs', 'kg']} />
      </Flex>
    </Box>
  );
}

function MonthRow({ entry, isOpen, setIsOpen, journalId, heightUnit, weightUnit, onSaved, setShowToast }) {
  const [height, setHeight] = useState(entry?.height != null ? String(entry.height) : '');

  const [weight, setWeight] = useState(entry?.weight != null ? String(entry.weight) : '');
  // Ounces only apply alongside a lbs weight (a sub-unit of the pound) — the kg
  // scale has no equivalent, so this is only stored/shown when weightUnit is 'lbs'.
  const [weightOz, setWeightOz] = useState(entry?.weightOz != null ? String(entry.weightOz) : '');
  const debounceRef = useRef(null);
  const toastTimerRef = useRef(null);
  const latestHeight = useRef(height);
  const latestWeight = useRef(weight);
  const latestWeightOz = useRef(weightOz);

  const localHasData = height !== '' || weight !== '' || weightOz !== '';

  const showSavedToast = () => {
    setShowToast(true);
    clearTimeout(toastTimerRef.current);
  };

  const saveEntry = useCallback(async (h, w, oz) => {
    const hVal = parseFloat(h);
    // kg keeps decimal precision (e.g. 4.5); lbs is a whole number now that oz
    // carries the fractional part in its own field.
    const wVal = weightUnit === 'kg' ? parseFloat(w) : parseInt(w, 10);
    const ozVal = parseInt(oz, 10);

    // if (isNaN(hVal) && isNaN(wVal)) return;
    const attributes = {
      height: isNaN(hVal) ? null : Math.round(hVal * 10) / 10,
      weight: weightUnit === 'kg' ? (isNaN(wVal) ? null : Math.round(wVal * 10) / 10) : null,
      'weight-pounds': weightUnit === 'lbs' ? isNaN(wVal) ? null : Math.round(wVal * 10) / 10 : null,
      'weight-ounces': weightUnit === 'lbs' ? (isNaN(ozVal) ? null : ozVal) : null,
    }

    await patch(`/v3/child_journals/${journalId}/growth_chart_entries/${entry.id}`, {
      data: {
        attributes,
      },
    });
    showSavedToast();
    setTimeout(() => setShowToast(false), 3000)
    if (onSaved) onSaved(attributes);
  }, [journalId, entry.id, onSaved, weightUnit]);

  const scheduleDebounce = () => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      saveEntry(latestHeight.current, latestWeight.current, latestWeightOz.current);
    }, 2000);
  };

  const handleHeightChange = (e) => {
    const v = e.target.value;
    setHeight(v);
    latestHeight.current = v;
    scheduleDebounce();
  };

  const handleWeightChange = (e) => {
    const v = e.target.value;
    setWeight(v);
    latestWeight.current = v;
    scheduleDebounce();
  };

  const handleWeightOzChange = (e) => {
    const v = e.target.value;
    setWeightOz(v);
    latestWeightOz.current = v;
    scheduleDebounce();
  };

  const handleBlur = () => {
    clearTimeout(debounceRef.current);
    saveEntry(latestHeight.current, latestWeight.current, latestWeightOz.current);
  };

  useEffect(() => () => {
    clearTimeout(debounceRef.current);
    clearTimeout(toastTimerRef.current);
  }, []);

  const displaySummary = localHasData
    ? [
      height ? `${parseFloat(height).toFixed(1)} ${heightUnit}` : null,
      weight
        ? weightUnit === 'lbs'
          ? `${parseFloat(weight).toFixed(0)} lbs ${weightOz ? parseInt(weightOz, 10) || 0 : 0} oz`
          : `${parseFloat(weight).toFixed(1)} ${weightUnit}`
        : null,
    ].filter(Boolean).join(' - ')
    : '';

  return (
    <Box p="20px" sx={{
      mb: 12,
      borderRadius: '10px',
      border: localHasData ? '1px solid #E2E8F0' : '1px dashed #CACFD8',
      borderBottom: '1px solid #E2E8F0',
      boxShadow: localHasData && '4px 4px 4px 0 rgba(133, 79, 155, 0.25)',
      background: localHasData ? 'linear-gradient(90deg, #F3E8FF 0%, #FCE7F3 100%);' : 'transparent',
    }}>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        onClick={() => {
          if (isOpen === entry.id) setIsOpen(null)
          else setIsOpen(entry.id)
        }}
        sx={{
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <Flex alignItems="center" flex={1} mr={2}>
          {localHasData ? (
            <Box as={IoMdCheckmark} color="#fff" size={20} bg="#9B59B6" sx={{ borderRadius: '50%', p: '4px' }} mr={1} />
          ) :
            <Box color="#fff" size={20} sx={{ borderRadius: '50%', p: '4px', border: '2px solid #CACFD8' }} mr={1} />}
          <Text
            fontFamily="Poppins"
            fontSize="16px"
            fontWeight={500}
            color={localHasData ? '#763099;' : '#64748B'}
          >
            {entry.label}
          </Text>
          {displaySummary && (
            <Text ml={2} fontFamily="Poppins" fontSize="16px" fontWeight={500} color={'rgba(118, 48, 153, 0.60)'}>
              {displaySummary}
            </Text>
          )}
        </Flex>
        <Flex alignItems="center" sx={{ flexShrink: 0 }}>
          <Box color={localHasData ? '#9B59B6' : '#64748B'}>
            {isOpen === entry.id ? <IoChevronUpOutline size={20} /> : <IoChevronDownOutline size={20} />}
          </Box>
        </Flex>
      </Flex>

      {isOpen === entry.id && (
        <Box pt={3} >
          <Flex sx={{ gap: '16px' }}>
            <Box >
              <Text fontFamily="Poppins" fontSize="16px" fontWeight={500} color="#0E0E0E" mb={1}>
                Height ({heightUnit})
              </Text>
              <Input
                type="number"
                step="0.1"
                min="0"
                value={height}
                onChange={handleHeightChange}
                onBlur={handleBlur}
                placeholder="0.0"
                fontFamily="Poppins"
                fontSize="16px"
                maxWidth={153}
                sx={{
                  boxShadow: '0 1px 2px 0 rgba(10, 13, 20, 0.03)',
                  border: '1px solid #CACFD8',
                  borderRadius: '31px',
                  p: '8px 12px',
                  bg: 'transparent',
                  '&:focus': { outline: 'none', borderColor: '#9B59B6' },
                }}
              />
            </Box>
            <Box >
              <Text fontFamily="Poppins" fontSize="16px" fontWeight={500} color="#0E0E0E" mb={1}>
                {weightUnit === 'lbs' ? 'Weight (lbs) (oz)' : `Weight (${weightUnit})`}
              </Text>
              <Flex sx={{ gap: '8px' }}>
                <Input
                  type="number"
                  step={weightUnit === 'lbs' ? '1' : '0.1'}
                  min="0"
                  maxWidth={weightUnit === 'lbs' ? 90 : 153}
                  value={weight}
                  onChange={handleWeightChange}
                  onBlur={handleBlur}
                  placeholder={weightUnit === 'lbs' ? '0' : '0.0'}
                  fontFamily="Poppins"
                  fontSize="16px"
                  sx={{
                    boxShadow: '0 1px 2px 0 rgba(10, 13, 20, 0.03)',
                    border: '1px solid #CACFD8',
                    borderRadius: '31px',
                    p: '8px 12px',
                    bg: 'transparent',
                    '&:focus': { outline: 'none', borderColor: '#9B59B6' },
                  }}
                />
                {weightUnit === 'lbs' && (
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    max="15"
                    maxWidth={90}
                    value={weightOz}
                    onChange={handleWeightOzChange}
                    onBlur={handleBlur}
                    placeholder="0"
                    fontFamily="Poppins"
                    fontSize="16px"
                    sx={{
                      boxShadow: '0 1px 2px 0 rgba(10, 13, 20, 0.03)',
                      border: '1px solid #CACFD8',
                      borderRadius: '31px',
                      p: '8px 12px',
                      bg: 'transparent',
                      '&:focus': { outline: 'none', borderColor: '#9B59B6' },
                    }}
                  />
                )}
              </Flex>
            </Box>
          </Flex>
        </Box>
      )}
    </Box>
  );
}

function formatAgeLabel(value) {
  return value
    .split('_')
    .map(word =>
      word.toLowerCase() === 'newborn'
        ? 'New Born'
        : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(' ');
}

function ageToSortOrder(age) {
  if (!age) return 999;
  const lower = age.toLowerCase();
  if (lower === 'newborn') return 0;
  const match = lower.match(/^(\d+)/);
  return match ? parseInt(match[1], 10) : 999;
}

function GrowthChart() {
  const params = useParams();
  const { entity } = useEntity(params);
  useSWR(`/v3/child_journals/${params.id}`);
  const { goBack } = useHistory();

  const firstBookId = entity?.books?.[0]?.id || '';
  const journalId = params?.id

  const { width } = useWindowSize();
  const isMobile = width < 600;

  const [tab, setTab] = useState('entries');
  const [showFTUE, setShowFTUE] = useState(false);
  const [heightUnit, setHeightUnit] = useState('in');
  const [weightUnit, setWeightUnit] = useState('lbs');
  const [showUnitsPanel, setShowUnitsPanel] = useState(false);
  const [zoom, setZoom] = useState(null);
  const previewContainerRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [isOpen, setIsOpen] = useState(null);
  const [entries, setEntries] = useState(FALLBACK_MONTHS.map((label, i) => ({
    id: String(i),
    age: label,
    label,
    height: null,
    weight: null,
    weightOz: null,
  })));
  const { data: front_cover_templates_data } = useSWR(`/v3/books/${firstBookId}/page_templates/growth_chart`)

  const fronCoverLayers = (front_cover_templates_data?.data?.included || []).filter(({ type }) => type === 'page-template-layers')
  const frontCoverEntity = front_cover_templates_data?.data?.data?.attributes

  useEffect(() => {
    if (tab !== 'preview' || !frontCoverEntity?.width || !frontCoverEntity?.height) return;
    const el = previewContainerRef.current;
    if (!el) return;
    const availableWidth = el.getBoundingClientRect().width;
    const availableHeight = window.innerHeight - 120;
    const scaleByWidth = availableWidth / frontCoverEntity.width;
    const scaleByHeight = availableHeight / frontCoverEntity.height;
    setZoom(Math.min(scaleByWidth, scaleByHeight) * 100);
  }, [tab, frontCoverEntity, width]);

  const { data: entriesData, revalidate } = useSWR(
    `/v3/child_journals/${journalId}/growth_chart`,
    getSWR
  );
  // const { data: prefsData, revalidate: revalidatePrefs } = useSWR(
  //   `/v3/child_journals/${journalId}/growth_chart_preferences`,
  //   getSWR
  // );

  useEffect(() => {
    const prefs = entriesData?.data?.data?.attributes;
    if (prefs) {
      if (prefs['height-measurement-system']) setHeightUnit(prefs['height-measurement-system'] === 'imperial' ? 'in' : 'cm');
      if (prefs['weight-measurement-system']) setWeightUnit(prefs['weight-measurement-system'] === 'imperial' ? 'lbs' : 'kg');
    }
    if (entriesData?.data?.included.length > 0) {
      setEntries((entriesData?.data?.included?.map(({ id, attributes }) => {
        const heightStr = attributes?.height || undefined;
        const heightNum = Number(heightStr);

        const heightResult = isNaN(heightNum)
          ? null : Number.isInteger(heightNum)
            ? heightNum
            : Number(heightNum.toFixed(1))

        // Falls back to 'weight' rather than branching on the weightUnit state directly —
        // this effect only depends on entriesData, and weightUnit (set from prefs earlier
        // in this same effect) wouldn't be updated yet in this closure on first load.
        const widthStr = attributes?.['weight-pounds'] || attributes?.['weight'] || undefined;
        const widthNum = Number(widthStr);

        const widthResult = isNaN(widthNum)
          ? null
          : prefs['weight-measurement-system'] === 'metric'
            ? Number(widthNum.toFixed(1))
            : Number.isInteger(widthNum)
              ? widthNum
              : Number(widthNum.toFixed(1))

        const weightOzStr = attributes?.['weight-ounces'] || undefined;
        const weightOzNum = Number(weightOzStr);
        const weightOzResult = isNaN(weightOzNum) ? null : Math.round(weightOzNum);

        return {
          id,
          age: attributes?.['age'],
          label: formatAgeLabel(attributes?.['age']),
          height: heightResult,
          weight: widthResult,
          weightOz: weightOzResult,
        }
      })).sort((a, b) => ageToSortOrder(a.age) - ageToSortOrder(b.age)));

    }
  }, [entriesData]);

  useEffect(() => {
    if (!store.get('growth_chart_ftue_watched')) {
      setShowFTUE(true);
    }
  }, []);

  const handleFTUEClose = async ({ heightUnit: hu, weightUnit: wu }) => {
    setHeightUnit(hu);
    setWeightUnit(wu);
    store.set('growth_chart_ftue_watched', true);
    setShowFTUE(false);
    await patch(`/v3/child_journals/${journalId}/growth_chart`, {
      data: {
        attributes: {
          'height-measurement-system': hu === 'cm' ? 'metric' : 'imperial',
          'weight-measurement-system': wu === 'lbs' ? 'imperial' : 'metric'
        }
      },
    });
    revalidate();
  };

  const handleHeightUnitChange = async (v) => {
    setHeightUnit(v);
    const { data } = await patch(`/v3/child_journals/${journalId}/growth_chart`, {
      data: {
        attributes: {
          'height-measurement-system': v === 'cm' ? 'metric' : 'imperial',
          'weight-measurement-system': weightUnit === 'lbs' ? 'imperial' : 'metric'
        }
      },
    });
    revalidate()
  };

  const handleWeightUnitChange = async (v) => {
    setWeightUnit(v);
    await patch(`/v3/child_journals/${journalId}/growth_chart`, {
      data: {
        attributes: {
          'height-measurement-system': heightUnit === 'cm' ? 'metric' : 'imperial',
          'weight-measurement-system': v === 'lbs' ? 'imperial' : 'metric'
        }
      },
    });
    revalidate()
  };

  const completedCount = entries.filter(e => e.height != null || e.weight != null).length;

  const totalCount = entries.length;
  const progressPct = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const mobileOptions = [
    { label: 'Entries', key: 'entries', icon: (active) => <CgRuler size={20} color={active ? '#9B59B6' : '#64748B'} /> },
    { label: 'Page Preview', key: 'preview', icon: (active) => bookPreviewIcon(active ? '#9B59B6' : '#64748B') },
  ];

  if (Object.keys(entity).length === 0)
    return <Box m={isMobile ? 2 : 4}><Loading /></Box>;

  if (entity?.['child-journal-growth-chart-permitted'] === false)
    return (
      <Box m={isMobile ? 0 : 2} >
        <Box
          sx={isMobile ? { bg: '#fff', position: 'fixed', bottom: 0, left: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' } : {}}
          p={2}
        >
          <Flex
            height={40}
            width="min-content"
            justifyContent="space-around"
            alignItems="center"
            sx={{ borderRadius: 'medium', cursor: 'pointer' }}
            onClick={goBack}
          >
            <Text as={IoIosArrowBack} color="mineShaft" size={24} />
            <Text minWidth={'max-content'} color="mineShaft" fontFamily="Poppins" lineHeight={'150%'} ml={1} fontWeight={400} fontSize="15px">
              Back
            </Text>
          </Flex>
        </Box>
        <PaymentBlocker sku="by-feature-growthchart" />
      </Box>
    );

  return (
    <Box m={isMobile ? 2 : 4} mr={2} pb={isMobile ? '80px' : 0}>
      {(showFTUE && completedCount === 0) && <GrowthChartFTUE onClose={handleFTUEClose} />}
      {showToast && (
        <Flex
          alignItems="center"
          mr={2}
          px={2}
          py={1}
          bg="#C5FFD5"
          sx={{
            borderRadius: '20px', width: '143px', height: '44px',
            position: 'fixed',
            top: '50px',
            right: '50%',
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        >
          <IoMdCheckmarkCircle color="#00611F" size={16} />
          <Text ml={1} fontFamily="Poppins" fontSize="12px" color="#00611F" fontWeight={600}>
            Entry saved!
          </Text>
        </Flex>
      )}
      {/* Header */}
      {!isMobile && <Flex justifyContent="space-between" alignItems="center" mb={3}>
        <Text fontFamily="Poppins" color="#000000" fontSize="22px" letterSpacing="0.22px" lineHeight="165%">
          {tab === 'entries' ? "Entries" : "Page Preview"}
        </Text>

        {tab === 'entries' ? (
          <Button
            height={44}
            py="unset"
            bg="transparent"
            color="#9B59B6"
            sx={{
              borderRadius: '23px',
              border: '1.5px solid #9B59B6',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={() => setTab('preview')}
          >
            {bookPreviewIcon()}&nbsp;Page Preview
          </Button>
        ) : (
          <Button
            height={44}
            py="unset"
            bg="transparent"
            color="#9B59B6"
            sx={{
              borderRadius: '23px',
              border: '1.5px solid #9B59B6',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={() => setTab('entries')}
          >
            <CgRuler size={20} color="#9B59B6" />&nbsp;Entries
          </Button>
        )}
      </Flex>}

      <Box maxWidth={600} mx="auto">

        {/* Progress row */}
        <Flex alignItems="center" justifyContent="space-between" mb={1}>
          <Text fontFamily="Bitter" m="auto" color="#0E0E0E" fontSize="30px" letterSpacing="0.22px" lineHeight="165%">
            Growth Chart
          </Text>
          <Box sx={{ position: 'relative' }}>
            <Box
              as="button"
              onClick={() => setShowUnitsPanel(o => !o)}
              sx={{
                bg: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                color: '#64748B',
                p: 1,
              }}
            >
              <IoSettingsOutline size={20} />
            </Box>
            {showUnitsPanel && (
              <UnitsPanel
                heightUnit={heightUnit}
                weightUnit={weightUnit}
                onHeightUnit={handleHeightUnitChange}
                onWeightUnit={handleWeightUnitChange}
                onClose={() => setShowUnitsPanel(false)}
              />
            )}
          </Box>
        </Flex>
        <Box mb={4}>
          {/* Progress bar */}
          <Box bg="#E2E8F0" sx={{ borderRadius: '4px', height: '6px' }}>
            <Box
              sx={{
                background: "linear-gradient(90deg, #9B59B6 0%, #FF6B9D 100%)",
                borderRadius: '4px',
                height: '100%',
                width: `${progressPct}%`,
                transition: 'width 0.3s ease',
              }}
            />
          </Box>
          <Text textAlign="center" lineHeight={"20px"} my={2} fontFamily="Poppins" fontSize="16px" color="#64748B">
            {completedCount} of {totalCount} entries completed
          </Text>
        </Box>

        {tab === 'entries' && (
          <>

            {/* Month accordion list */}
            <Box sx={{ overflow: 'hidden' }}>
              {entries.map(entry => (
                <MonthRow
                  key={entry.id + entry?.height + entry?.weight + entry?.weightOz}
                  entry={entry}
                  journalId={journalId}
                  heightUnit={heightUnit}
                  weightUnit={weightUnit}
                  onSaved={(attributes) => {
                    // `entry` here is the row that just saved (captured from the outer
                    // .map below) — the inner callback param is named `e` so it can't
                    // shadow it, which previously made every row's id compare equal to
                    // itself and overwrote all rows with whichever one saved last.
                    setEntries(prevEntries => prevEntries.map((e) => {
                      if (e.id !== entry.id) return e;
                      return {
                        ...e,
                        height: attributes?.height,
                        weight: weightUnit === 'lbs' ? attributes?.['weight-pounds'] : attributes?.weight,
                        weightOz: attributes?.['weight-ounces'],
                      }
                    }))
                    revalidate()
                  }}
                  setShowToast={setShowToast}
                  setIsOpen={setIsOpen}
                  isOpen={isOpen}
                />
              ))}
            </Box>
          </>
        )}

        {/* Mobile bottom tab bar */}
        {isMobile && (
          <Flex
            bg="white"
            justifyContent="space-around"
            alignItems="center"
            p="17px 0"
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              width: '100vw',
              zIndex: 999,
              border: '1.5px solid #F5EEF8',
            }}
          >
            {mobileOptions.map(({ icon, label, key }) => {
              const isSelected = tab === key;
              return (
                <Flex
                  key={key}
                  onClick={() => setTab(key)}
                  ml="24px"
                  bg="white"
                  sx={{ borderRadius: '100px', cursor: 'pointer' }}
                >
                  {icon(isSelected)}
                  <Text
                    ml={1}
                    fontSize="16px"
                    fontFamily="Poppins"
                    fontWeight={500}
                    lineHeight="20px"
                    color={isSelected ? '#9B59B6' : '#64748B'}
                  >
                    {label}
                  </Text>
                </Flex>
              );
            })}
          </Flex>
        )}
      </Box>
      {tab === 'preview' && (
        <Box id="Preview" ref={previewContainerRef}>
          {previewLoading ? (
            <Flex justifyContent="center" alignItems="center" height="400px">
              <Text fontFamily="Poppins" color="#64748B" fontSize="16px">
                Loading preview...
              </Text>
            </Flex>
          ) : fronCoverLayers.length > 0 && zoom != null ? (
            <Box
              sx={{
                width: `${frontCoverEntity?.width * zoom / 100}px`,
                height: `${frontCoverEntity?.height * zoom / 100}px`,
                overflow: 'hidden',
                m: 'auto',
                boxShadow: '0px 7px 36px 5px rgba(0, 0, 0, 0.25)',
              }}
            >
              <Flex
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                sx={{
                  transform: `scale(${zoom / 100})`,
                  transformOrigin: 'top left',
                  MozTransform: `scale(${zoom / 100})`,
                  MozTransformOrigin: '0 0',
                  width: `${frontCoverEntity?.width}px`,
                  height: `${frontCoverEntity?.height}px`,
                  touchAction: 'pan-x pan-y pinch-zoom',
                  bg: frontCoverEntity?.['background-colour']
                }}
                id="templateContainer"
              >
                {fronCoverLayers && nestImageLayers(fronCoverLayers).map(({ id, type, children, attributes, relationships }) => {

                  const componentValue = attributes["component-value"]; // "1_month_height"
                  let value = '-'

                  const match = componentValue.match(/^(.*)_(height|weight)$/);

                  if (match) {
                    const [, age, field] = match;
                    const record = entries.find(item => item.age === age);

                    if (field === 'weight' && record?.weight != null) {
                      value = weightUnit === 'lbs'
                        ? `${record.weight.toFixed(0)} lbs ${record.weightOz || 0} oz`
                        : `${record.weight.toFixed(1)} ${weightUnit}`;
                    } else if (field === 'height' && record?.height != null) {
                      value = `${record.height.toFixed(1)} ${heightUnit}`;
                    } else {
                      value = '-';
                    }
                  }

                  return <Box key={id} id={id}>

                    {attributes?.type === "Books::PageTemplateImageLayer" ? <Box id={attributes?.type}>
                      <ImageLayer
                        {...attributes}
                        children={children}
                        attributes={attributes}
                        photo={Object.keys(relationships?.['canvas-image'] || {}).length > 0 ? relationships?.['canvas-image']?.data : relationships?.['photo']?.data}
                        zoom={zoom}
                      />
                    </Box>
                      : <Box id={attributes?.id}> <TextLayer
                        {...attributes}
                        zoom={zoom}
                        xpos={attributes?.xpos}
                        ypos={attributes?.ypos}
                        fontAlignment={attributes?.['font-alignment']}
                        fontFamily={attributes?.['font-name']}
                        fontWeight={attributes?.['font-weight']}
                        fontSize={attributes?.['font-size']}
                        color={attributes?.['font-colour']}
                        text={value}
                      />
                      </Box>}
                  </Box>
                })}
              </Flex>
            </Box>
          ) : (
            <Flex
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              height="400px"
              bg="#F8F4FB"
              sx={{ borderRadius: '16px', border: '1px dashed #9B59B6' }}
            >
              <CgRuler size={48} color="#9B59B6" />
              <Text mt={3} fontFamily="Poppins" fontSize="16px" color="#64748B" textAlign="center">
                Preview not available yet
              </Text>
              <Text mt={1} fontFamily="Poppins" fontSize="14px" color="#94A3B8" textAlign="center">
                Add some entries first to generate a preview
              </Text>
            </Flex>
          )}
        </Box>
      )}
    </Box>
  );
}

export default GrowthChart;
